import {
  type Baggage,
  type Context,
  context,
  propagation,
  Span,
  SpanKind,
  SpanStatusCode,
  trace,
} from '@opentelemetry/api';

import {getBrowserOrigin, isBrowserRuntime} from '../runtime/browser';
import {getRequestUserContext} from '../runtime/userContext';
import type {RequestMethod, RequestUserContext} from '../types/request';
import {RequestExecutionMode, type ResolvedRequest} from '../types/route';
import {sanitizeHeadersForLog} from '../utils/headers';
import {safeSerialize, truncateForAttribute} from '../utils/json';
import {
  redactSensitiveValue,
  sanitizeQueryForLog,
  sanitizeUrlForLog,
} from '../utils/redaction';

import {FLUSH_DEBOUNCE_MS} from './constants';
import {FlushableProvider} from './types';

// This should be defined here cause for catching the flush timestamp we need to have a single instance of the tracer.
let cachedTracer: ReturnType<typeof trace.getTracer> | undefined;
let lastFlushTimestamp = 0;

// Get the tracer for the current service
const getTracer = () => {
  if (cachedTracer) {
    return cachedTracer;
  }

  cachedTracer = trace.getTracer(
    process.env.NEXT_PUBLIC_OTEL_SERVICE_NAME || 'unknown',
  );

  return cachedTracer;
};

const buildRequestSpanName = ({
  method,
  resolvedRequest,
}: {
  method: RequestMethod;
  resolvedRequest: ResolvedRequest;
}): string => {
  return `http.client ${method} ${resolvedRequest.logicalPath}`;
};

const getBaggageUserContext = (): Partial<RequestUserContext> => {
  const baggage: Baggage | undefined = propagation.getBaggage(context.active());

  if (!baggage) {
    return {};
  }

  return {
    id: baggage.getEntry('user.id')?.value,
    role: baggage.getEntry('user.role')?.value,
    email: baggage.getEntry('user.email')?.value,
  };
};

/** Merges user context from three sources (explicit param > OTel baggage > global store) with first-wins precedence. */
const getMergedUserContext = (
  userContext?: Partial<RequestUserContext>,
): Partial<RequestUserContext> => {
  const baggageContext = getBaggageUserContext();
  const storedContext = getRequestUserContext();

  return {
    id: userContext?.id ?? baggageContext.id ?? storedContext?.id,
    role: userContext?.role ?? baggageContext.role ?? storedContext?.role,
    email: userContext?.email ?? baggageContext.email ?? storedContext?.email,
  };
};

/** Only injects W3C trace headers for same-origin or server-side requests to avoid leaking trace context to third parties. */
export const shouldInjectTracingHeaders = (
  resolvedRequest: ResolvedRequest,
  requestUrl: URL,
): boolean => {
  if (resolvedRequest.executionMode !== RequestExecutionMode.PASSTHROUGH) {
    return true;
  }

  const browserOrigin = getBrowserOrigin();

  if (!browserOrigin) {
    return true;
  }

  return requestUrl.origin === browserOrigin;
};

export const injectTracingHeaders = ({
  headers,
  resolvedRequest,
  requestUrl,
  requestContext = context.active(),
}: {
  headers: Headers;
  resolvedRequest: ResolvedRequest;
  requestUrl: URL;
  requestContext?: Context;
}): void => {
  if (!shouldInjectTracingHeaders(resolvedRequest, requestUrl)) {
    return;
  }

  propagation.inject(requestContext, headers);
};

export const getRequestSpanContext = (
  span: Span,
  parentContext: Context = context.active(),
): Context => {
  return trace.setSpan(parentContext, span);
};

export const withRequestSpanContext = <T>(
  span: Span,
  callback: () => T,
  parentContext: Context = context.active(),
): T => {
  return context.with(getRequestSpanContext(span, parentContext), callback);
};

/** Opens an OTel CLIENT span for the outgoing request, recording URL, method, route family, auth mode, and user context. */
export const startRequestSpan = ({
  method,
  requestUrl,
  resolvedRequest,
  headers,
  body,
  userContext,
}: {
  method: RequestMethod;
  requestUrl: URL;
  resolvedRequest: ResolvedRequest;
  headers: HeadersInit | Record<string, string>;
  body?: unknown;
  userContext?: Partial<RequestUserContext>;
}): Span => {
  const tracer = getTracer();
  const sanitizedRequestUrl = sanitizeUrlForLog(requestUrl);
  const sanitizedRequestBody =
    body === undefined ? undefined : redactSensitiveValue(body);
  const span = tracer.startSpan(
    buildRequestSpanName({
      method,
      resolvedRequest,
    }),
    {
      kind: SpanKind.CLIENT,
      attributes: {
        'resource.name': `${requestUrl.origin}${requestUrl.pathname}`,
        'net.peer.name': requestUrl.hostname,
        'net.peer.port': requestUrl.port,
        'http.url': sanitizedRequestUrl,
        'http.host': requestUrl.host,
        'http.scheme': requestUrl.protocol.replace(':', ''),
        'http.method': method,
        'http.query': sanitizeQueryForLog(requestUrl),
        'http.request.headers': safeSerialize(sanitizeHeadersForLog(headers)),
        'request.route.family': resolvedRequest.family,
        'request.execution.mode': resolvedRequest.executionMode,
        'request.auth.mode': resolvedRequest.authMode,
        'request.auth.token_source': resolvedRequest.tokenSource,
        ...(resolvedRequest.environment
          ? {
              'request.plutus.environment': resolvedRequest.environment,
            }
          : {}),
        ...(sanitizedRequestBody !== undefined
          ? {
              'http.request.body': truncateForAttribute(
                safeSerialize(sanitizedRequestBody),
              ),
            }
          : {}),
      },
    },
    context.active(),
  );
  const mergedUserContext = getMergedUserContext(userContext);

  if (mergedUserContext.id) {
    span.setAttribute('user.id', mergedUserContext.id);
  }

  if (mergedUserContext.role) {
    span.setAttribute('user.role', mergedUserContext.role);
  }

  if (mergedUserContext.email) {
    span.setAttribute('user.email', mergedUserContext.email);
    span.setAttribute('usr.email', mergedUserContext.email);
  }

  span.addEvent('request.resolve.route_matched', {
    family: resolvedRequest.family,
    mode: resolvedRequest.executionMode,
    token_source: resolvedRequest.tokenSource,
  });

  return span;
};

export const setResponseAttributes = ({
  span,
  response,
  parsedData,
}: {
  span: Span;
  response: Response;
  parsedData: unknown;
}): void => {
  span.setAttribute('http.status_code', response.status);
  span.setAttribute(
    'http.response.headers',
    safeSerialize(sanitizeHeadersForLog(response.headers)),
  );

  if (parsedData === undefined) {
    return;
  }

  span.addEvent('request.response.parse');
  span.setAttribute(
    'http.response.body',
    truncateForAttribute(safeSerialize(redactSensitiveValue(parsedData))),
  );
};

export const setSpanError = ({
  span,
  error,
  status,
}: {
  span: Span;
  error: unknown;
  status?: number;
}): void => {
  const message =
    error instanceof Error
      ? error.message
      : safeSerialize(redactSensitiveValue(error));

  span.setStatus({
    code: SpanStatusCode.ERROR,
    message,
  });
  span.addEvent('request.error', {
    message,
  });
  span.setAttributes({
    'http.error': true,
    'http.error.message': message,
    ...(status !== undefined ? {'http.status_code': status} : {}),
  });
};

/** Checks if the provider is a FlushableProvider. that means it has a forceFlush method. */
const isFlushableProvider = (
  provider: unknown,
): provider is FlushableProvider => {
  return (
    typeof provider === 'object' &&
    provider !== null &&
    'forceFlush' in provider &&
    typeof (provider as FlushableProvider).forceFlush === 'function'
  );
};

/**
 * Flushes buffered browser spans immediately so error traces are not missing
 * from the collector. The BatchSpanProcessor batches spans on a 1s interval,
 * which can cause the browser client span to arrive after the server has
 * already exported its child spans — resulting in a "missing span" in the trace.
 * Debounced to avoid redundant flushes when multiple requests fail concurrently.
 * Only runs in the browser; server-side spans are flushed synchronously.
 */
export const flushSpansOnError = (): void => {
  if (!isBrowserRuntime()) {
    return;
  }

  const now = Date.now();

  // If the last flush was less than the debounce time, return early.
  if (now - lastFlushTimestamp < FLUSH_DEBOUNCE_MS) {
    return;
  }

  lastFlushTimestamp = now;

  const provider = trace.getTracerProvider();

  if (isFlushableProvider(provider)) {
    provider.forceFlush().catch(() => undefined);
  }
};
